// Ripple + Magnetic 효과 버튼 (reduced-motion 자동 비활성)
import { forwardRef, useRef } from 'react'
import type { ButtonHTMLAttributes, MouseEvent } from 'react'

type Variant = 'primary' | 'ghost' | 'accent' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  magnetic?: boolean
}

const variantClass: Record<Variant, string> = {
  primary: 'btn',
  ghost: 'btn btn-ghost',
  accent: 'btn btn-accent',
  danger: 'btn btn-danger',
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', magnetic = true, className = '', children, onClick, onMouseMove, onMouseLeave, ...rest },
  ref,
) {
  const inner = useRef<HTMLButtonElement | null>(null)
  const setRef = (el: HTMLButtonElement | null) => {
    inner.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el
  }

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    const el = inner.current
    if (el && !prefersReducedMotion()) {
      const rect = el.getBoundingClientRect()
      const r = document.createElement('span')
      r.className = 'ripple'
      r.style.left = `${e.clientX - rect.left}px`
      r.style.top = `${e.clientY - rect.top}px`
      el.appendChild(r)
      setTimeout(() => r.remove(), 650)
    }
    onClick?.(e)
  }

  const handleMove = (e: MouseEvent<HTMLButtonElement>) => {
    onMouseMove?.(e)
    const el = inner.current
    if (!el || !magnetic || prefersReducedMotion()) return
    const rect = el.getBoundingClientRect()
    const dx = e.clientX - (rect.left + rect.width / 2)
    const dy = e.clientY - (rect.top + rect.height / 2)
    el.style.transform = `translate(${dx * 0.18}px, ${dy * 0.18}px)`
  }
  const handleLeave = (e: MouseEvent<HTMLButtonElement>) => {
    onMouseLeave?.(e)
    if (inner.current) inner.current.style.transform = ''
  }

  return (
    <button
      ref={setRef}
      className={`${variantClass[variant]} ${className}`.trim()}
      onClick={handleClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      {...rest}
    >
      {children}
    </button>
  )
})
